<template>
    <nav>
        <ul class="tabs">
            <li
                v-for="(route, i) in allAppRoutes"
                :key="route.name"
            >
                <router-link
                    custom
                    :to="route"
                    v-slot="{ navigate, href, isActive }"
                >
                    <a
                        @click="isDisabled(route) ? null : navigate"
                        :href="isDisabled(route) ? null : href"
                        :class="{
                            'menu-item': true,
                            'disabled': isDisabled(route),
                            'active': isActive
                        }"
                        :accesskey="i + 1"
                    >
                        <button
                            type="button"
                            class="btn link status-container"
                            v-if="!isDisabled(route) && hasStatus(route)"
                            @click="getMetaEntry(route).toggleValidation()"
                        >
                            <img v-if="getMetaEntry(route).getStatus() === getAllStatus().VALIDATED" src="@/assets/pictos/menu-status/validated.svg" alt="" />
                            <img v-if="getMetaEntry(route).getStatus() === getAllStatus().NEEDS_VALIDATION" src="@/assets/pictos/menu-status/needs_validation.svg" alt="" />
                            <img v-if="getMetaEntry(route).getStatus() === getAllStatus().INVALID" src="@/assets/pictos/menu-status/invalid.svg" alt="" />
                        </button>

                        <img class="picto" v-if="hasPicto(route)" :src="getMetaEntry(route).getPicto()" alt="" />

                        <span class="title">
                            <strong class="long">{{ $t(`menuEntries.${String(route.name)}.long`) }}</strong>
                            <strong class="short">{{ $t(`menuEntries.${String(route.name)}.short`) }}</strong>
                        </span>
                    </a>
                </router-link>
            </li>
        </ul>
    </nav>
</template>

<script lang="ts">
import { BaseMenuEntry, MenuEntryStatus } from '@/utilities/menu-entries/base-menu-entry';
import { defineComponent } from 'vue';
import { RouteRecordRaw, useRouter } from 'vue-router';

export default defineComponent({
    setup()
    {
        const router = useRouter();

        // Fetch all children routes of the route named "app"
        const allAppRoutes = router.getRoutes().find(item => (item.name === 'app'))?.children ?? [];

        function hasMetaEntry(route: RouteRecordRaw)
        {
            if (!route.meta?.entry) {
                return false;
            }

            return route.meta.entry instanceof BaseMenuEntry;
        }

        function getMetaEntry(route: RouteRecordRaw) : BaseMenuEntry
        {
            if (!hasMetaEntry(route)) {
                throw new Error("Trying to get metaEntry of a menu entry that doesn't have one");
            }

            return route.meta?.entry as BaseMenuEntry;
        }

        function isDisabled(route: RouteRecordRaw)
        {
            return hasMetaEntry(route) && !getMetaEntry(route).isEnabled();
        }

        function hasStatus(route: RouteRecordRaw)
        {
            return hasMetaEntry(route) && getMetaEntry(route).getStatus() !== MenuEntryStatus.NONE;
        }

        function hasPicto(route: RouteRecordRaw)
        {
            return hasMetaEntry(route);
        }

        function getAllStatus()
        {
            return MenuEntryStatus;
        }

        return {
            allAppRoutes,
            hasMetaEntry,
            getMetaEntry,
            isDisabled,
            hasStatus,
            hasPicto,
            getAllStatus,
        };
    },
})
</script>

<style lang="scss" scoped>

.tabs {
    display: flex;
    align-items: stretch;
    margin: 0;
    padding: 0;
    list-style-type: none;

    & > li {
        flex: 1 1 0;
    }

    // Arrow on the left of each menu-item and border
    & > li:not(:last-child) > .menu-item {
        border-right: 1px solid $gray-100;

        &::before,
        &::after {
            content: "";
            position: absolute;
            left: 100%;
            z-index: 10;
            clip-path: polygon(0% 0%, 100% 50%, 0% 100%);    // A triangle
        }
        &::before {
            top: calc(50% - 0.625rem - 1px);
            width: calc(0.625rem + 1px);
            height: calc(1.25rem + 2px);
            background-color: $gray-100;
        }
        &::after {
            top: calc(50% - 0.625rem);
            width: 0.625rem;
            height: 1.25rem;
        }
    }

    & > li > .menu-item {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: center;
        height: 100%;
        padding: 2.75rem 1rem 0.55rem 1rem;
        border-top: 1px solid $gray-100;
        border-bottom: 1px solid $gray-100;

        // Contained items
        & > .picto {
            width: 45%;
            max-width: 3.125rem;
            max-height: 3.25rem;
        }

        & > .title {
            margin-top: 1.25rem;
            word-break: break-all;
            text-align: center;

            @include media-breakpoint-up(lg) {
                & > .short {
                    display: none;
                }
            }

            @include media-breakpoint-down(lg) {
                & > .long {
                    display: none;
                }
            }
        }

        & > .status-container {
            position: absolute;
            top: 0.5rem;
            right: 0.5rem;
            width: 1.8rem;
            height: 1.8rem;

            & > img {
                width: 100%;
            }
        }

        // Contextual styles
        & {
            color: $gray-700;

            &, &::after {
                background-color: $gray-200;
            }
        }

        &.active {
            &, &::after {
                background-color: $gray-100;
            }
        }

        &.disabled {
            & > * {
                opacity: 0.5;
            }
        }

        &:not(.disabled):hover {
            border-top-color: $blue-light;
            border-bottom-color: $blue-light;
            color: $white;

            &, &::after {
                background-color: $blue-light;
            }

            & > img {
                // Saturate brightness of the picto so it becomes white
                filter: brightness(1000%);
            }
        }
    }
}

</style>
